import {
  ApplicationView,
  StartControllerScope,
  useController,
} from '@aha-app/mvc';
import React, { useCallback, useState } from 'react';
import styles from './CreateRecordsModal.module.css';
import EditableList from './EditableList';
import SelectroV2 from '@aha-app/selectro-v2';
import { capitalize, singularize } from 'inflected';
import Modal, { ModalHeader } from '../utils/Modal';
import customElementBoolAttr from '../utils/customElementBoolAttr';
import terminology from '../utils/terminology';
import CreateRecordsController from './CreateRecordsController';
import { type CreateRecord } from '../shared-types';
import FeatureTypeSelect from '../components/FeatureTypeSelect';

const CreateRecordsModalContent = ApplicationView(() => {
  const controller = useController(CreateRecordsController);
  const {
    recordType,
    data,
    submitting,
    selectedProject,
    selectedRelease,
    selectedFeature,
  } = controller.state;
  const [items, setItems] = useState(data);

  const fields = controller.fieldsForRecordType;
  const disabled = submitting || !controller.areSelectionsValid;

  const handleSubmit = useCallback(() => {
    if (submitting) return;

    void controller.actionCreateRecords(items);
  }, [controller, items, submitting]);

  /**
   * Updates the text of the item in the local items state
   */
  const updateItem = useCallback((item: CreateRecord, value: string) => {
    setItems(items =>
      items.map(c => (c.id === item.id ? { ...c, text: value } : c))
    );
  }, []);

  return (
    <div className={styles.createRecordsModal}>
      <ModalHeader
        onClose={() => {
          controller.actionAbort();
        }}
        modalTitle={`Create ${terminology(recordType)}`}
      />
      <div className={styles.body}>
        {fields?.project && (
          <>
            <div className={styles.workspace}>
              <p className={styles.label}>
                Aha! workspace<span className={styles.required}>*</span>
              </p>
              <div className={styles.selectroWrapper}>
                <SelectroV2
                  fetchUrl='/api/v1/products/picker?teams=false&user_is=contributor'
                  type='single'
                  text={{
                    searchPlaceholder: 'Select a workspace',
                    noneSelected: 'Select a workspace',
                  }}
                  selected={
                    selectedProject?.id && selectedProject.name
                      ? {
                          value: selectedProject.id,
                          text: selectedProject.name,
                        }
                      : undefined
                  }
                  callbacks={{
                    select: ({ controller: selectroController }) => {
                      const selected = selectroController.selected;
                      if (Array.isArray(selected) && selected.length > 0) {
                        const item = selected[0];
                        if (item?.value) {
                          controller.actionSetProject({
                            id: item.value as `${number}`,
                            name: item.text || '',
                          });
                        }
                      }
                    },
                  }}
                />
              </div>
            </div>
          </>
        )}
        {fields?.release && (
          <>
            <div className={styles.workspace}>
              <p className={styles.label}>
                Release <span className={styles.required}>*</span>
              </p>
              <div className={styles.selectroWrapper}>
                <SelectroV2
                  fetchUrl={`/api/v1/releases/picker?write_access_only=1${
                    recordType === 'epics' ? '&for_epic_projects=true' : ''
                  }`}
                  type='single'
                  text={{
                    searchPlaceholder: 'Select a release',
                    noneSelected: 'Select a release',
                  }}
                  selected={
                    selectedRelease?.id && selectedRelease.name
                      ? {
                          value: selectedRelease.id,
                          text: selectedRelease.name,
                        }
                      : undefined
                  }
                  callbacks={{
                    select: ({ controller: selectroController }) => {
                      const selected = selectroController.selected;
                      if (Array.isArray(selected) && selected.length > 0) {
                        const item = selected[0];
                        if (item?.value) {
                          controller.actionSetRelease({
                            id: item.value as `${number}`,
                            name: item.text || '',
                          });
                        }
                      }
                    },
                  }}
                />
              </div>
            </div>
          </>
        )}
        {fields?.feature && (
          <div className={styles.workspace}>
            <p className={styles.label}>
              Feature <span className={styles.required}>*</span>
            </p>
            <div className={styles.selectroWrapper}>
              <SelectroV2
                fetchUrl={`/api/v1/features/picker?for_projects=${selectedProject?.id}`}
                type='single'
                text={{
                  searchPlaceholder: 'Select a feature',
                  noneSelected: 'Select a feature',
                }}
                selected={
                  selectedFeature?.id && selectedFeature.name
                    ? { value: selectedFeature.id, text: selectedFeature.name }
                    : undefined
                }
                callbacks={{
                  select: ({ controller: selectroController }) => {
                    const selected = selectroController.selected;
                    if (Array.isArray(selected) && selected.length > 0) {
                      const item = selected[0];
                      if (item?.value) {
                        controller.actionSetFeature({
                          id: item.value as `${number}`,
                          name: item.text || '',
                        });
                      }
                    }
                  },
                }}
              />
            </div>
          </div>
        )}
        {fields?.workflowKind && (
          <div className={styles.strings}>
            <p className={styles.label}>
              {capitalize(singularize(terminology(recordType)))} type{' '}
              <span className={styles.required}>*</span>
            </p>
            <FeatureTypeSelect
              releaseId={selectedRelease.id}
              currentId={controller.state.selectedWorkflowKind?.id}
              select={kind => {
                controller.actionSetWorkflowKind(kind);
              }}
            />
          </div>
        )}
        <div className={styles.strings}>
          <p className={styles.label}>
            {capitalize(singularize(terminology(recordType)))} name
          </p>
          <EditableList items={items} onItemChange={updateItem} />
        </div>
      </div>
      <aha-modal-footer>
        <aha-button
          kind='primary'
          type='button'
          disabled={customElementBoolAttr(disabled)}
          onClick={handleSubmit}
        >
          Create {terminology(recordType)}
        </aha-button>
      </aha-modal-footer>
    </div>
  );
});

const CreateRecordsModal = StartControllerScope(
  CreateRecordsController,
  ApplicationView(() => {
    const controller = useController(CreateRecordsController);

    return (
      <Modal
        onClose={() => {
          controller.actionAbort();
        }}
      >
        <CreateRecordsModalContent />
      </Modal>
    );
  })
);

export default CreateRecordsModal;
